import { PERMISSIONS } from '@23degrees/shared-code/config/permissions';
import { MessageBus } from '@23degrees/shared/bus';
import { ContentManager } from '@23degrees/shared/manager/content/content.manager';
import { GlobalService } from '@23degrees/shared/services/global/global.service';
import { AuthQuery } from '@23degrees/stores/auth/auth.query';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SPINNER_OVERLAY_MESSAGES } from '@config';
import { EnvironmentService } from '../env/env.service';
import { PaymentService } from '../payment/payment.service';

declare let Stripe: any;
declare let OctobatBeanie: any;

@Injectable({ providedIn: 'root' })
export class StripeService {
  private httpClient = inject(HttpClient);
  private authQuery = inject(AuthQuery);
  private paymentService = inject(PaymentService);
  private messageBus = inject(MessageBus);
  private contentManager = inject(ContentManager);
  private router = inject(Router);
  private globalService = inject(GlobalService);

  private envService: EnvironmentService = inject(EnvironmentService);

  stripe;
  beanie;
  key: any;
  octokey: any;
  stripeJS = 'assets/js/stripe/v3.js';
  beanieJS = 'assets/js/octobat/octobat-beanie.min.js';

  constructor() {
    // console.log("[StripeService] Constructor");
    if (this.globalService.isServer()) return;
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    this.setupOctobat();
  }

  setupStripe = async () => {
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    try {
      // console.log('[StripeService] Setup');
      const keyRes: any = await this.httpClient
        .get<any>(this.envService.StripeKeyApi, { headers: { 'Cache-Control': 'no-cache', NoAuth: 'true' } })
        .toPromise();

      this.key = keyRes.STRIPEKEY;
      const req = new HttpRequest('GET', this.stripeJS, { responseType: 'text' });
      // const req = new HttpRequest('GET', , { responseType: 'text' });
      const stripe: any = await this.httpClient.request(req).toPromise();
      const script = this.globalService.document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = (<any>stripe).body.toString();
      this.globalService.document.head.appendChild(script);
      if (!this.stripe) {
        this.stripeInit();
      }
    } catch (err) {
      console.log({ Error: err.message });
    }
  };

  setupOctobat = async () => {
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    try {
      // console.log('[StripeService] Octobat Setup');
      const keyRes: any = await this.httpClient
        .get<any>(this.envService.OctobatKeyApi, { headers: { 'Cache-Control': 'no-cache', NoAuth: 'true' } })
        .toPromise();

      this.octokey = keyRes.OCTOBATKEY;
      const req = new HttpRequest('GET', this.beanieJS, { responseType: 'text' });
      // const req = new HttpRequest('GET', 'https://cdn.jsdelivr.net/gh/0ctobat/octobat-beanie.js@latest/dist/octobat-beanie.min.js', { responseType: 'text' });
      const octobat: any = await this.httpClient.request(req).toPromise();
      const script = this.globalService.document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = (<any>octobat).body.toString();
      this.globalService.document.head.appendChild(script);
      if (!this.beanie) {
        this.beanieInit();
      }
    } catch (err) {
      console.log({ Error: err.message });
    }
  };

  setup = async () => {
    await this.setupStripe();
    await this.setupOctobat();
  };

  stripeInit = async () => {
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    if (typeof Stripe !== 'undefined') {
      // console.log('Initializing Stripe ...');
      this.stripe = Stripe(this.key);
    }
  };

  beanieInit = async () => {
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    if (typeof OctobatBeanie !== 'undefined') {
      this.beanie = OctobatBeanie(this.octokey || 'oc_test_pkey_KAdKDEWBDA6mvWexSU9xMwtt');
    }
  };

  getStripe = async () => {
    // console.log("[GetStripe] - Called");
    if (!this.stripe) {
      await this.setupStripe();
    }
    return this.stripe;
  };

  // Stripe Checkout
  startCheckoutSession = async (event) => {
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        preventOverlayClosing: true,
        showEmbed: false,
        loadingInfo: 'Checking out with stripe.com ...',
      },
    });

    const url = window.location.href;
    let retval;
    const { cart, mode } = event;
    const base = this.globalService.baseUrl();

    console.log('startCheckoutSession I', this.authQuery.getValue().user, this.authQuery.getValue().token);
    console.log('startCheckoutSession II', mode, url, base, cart);

    try {
      const { data } = await this.paymentService.createSession(mode, url, url, cart);
      const response: any = data;
      console.log('startCheckoutSession III', response);
      if (!this.stripe) {
        await this.setup();
      }
      if (response?.type && response.payload) {
        const { type, payload } = response;
        switch (type) {
          case 'checkoutSession': {
            this.saveCurrentState();
            const result = await this.beanie.redirectToBeanie({
              sessionId: payload.id,
            });
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            break;
          }
          case 'subscriptionUpdate':
            console.log('[updatedSubscription]', payload);
            retval = 'update';
            break;
        }
      }
      return retval;
    } catch (error) {
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      this.router.navigate(['/user/account']);
    }
  };

  startCheckoutSessionV2 = async (cart, mode) => {
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        preventOverlayClosing: true,
        showEmbed: false,
        loadingInfo: 'Checking out with stripe.com ...',
      },
    });
    const url = window.location.href;
    const base = this.globalService.baseUrl();
    let retval;

    try {
      console.log('[startCheckoutSessionV2]', { cart, mode, url, returnUrl: `${base}/user-products?status=checkout-success&url=${btoa(url)}` });
      const { data } = await this.paymentService.createSessionV2(mode, `${base}/user-products?status=checkout-success&url=${btoa(url)}`, url, cart);
      const response: any = data;

      if (!this.beanie) {
        await this.setupOctobat();
      }
      if (response?.type && response.payload) {
        const { type, payload } = response;
        switch (type) {
          case 'checkoutSession': {
            this.saveCurrentState();
            const result = await this.beanie.redirectToBeanie({
              sessionId: payload.id,
            });
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            break;
          }
          case 'subscriptionUpdate':
            // console.log('[updatedSubscription]', payload);
            setTimeout(async () => {
              this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
              this.router.navigate(['/user/plan-billing'], { queryParams: { status: 'update-success', url: btoa(url) } });
            }, 10000);
            retval = 'update';
            break;
          case 'legacyCustomer':
            this.router.navigate(['user/plan-billing'], { queryParams: { status: 'contact-us' } });
            break;
        }
      }
      return retval;
    } catch (error) {
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      this.router.navigate(['/user/account']); // Route to payment unsuccessful
    }
  };

  startSetupSession = async () => {
    if (this.authQuery.checkPermission(PERMISSIONS.NO_SCRIPTS)) return;
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        preventOverlayClosing: true,
        showEmbed: false,
        loadingInfo: 'Setting up stripe.com ...',
      },
    });
    const url = window.location.href;

    try {
      const { data } = await this.paymentService.createSetupSession(url);
      console.log('[startSetupSession]', { data });
      const result = await this.beanie.redirectToBeanie({
        sessionId: data.id,
      });
      console.log({ result });
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
    } catch (error) {
      console.log(error);
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
    }
  };

  private saveCurrentState = () => {
    const params = this.globalService.getParameters();
    if (params?.slug) {
      // console.log('[StripeService] - Slug to save state', params.slug);
      this.contentManager.saveSlugStateInSession(params.slug);
    }
  };
}
